/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable unicorn/consistent-destructuring */
import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Modal from 'react-modal';
import { XIcon } from '@heroicons/react/outline';
import Select from 'react-select';

import { useStores } from '../stores/RootStore';
import { useAppSelector } from '../stores/AppStore';
import { selectHasOneOfTheRoles } from '../features/user/userSlice';

import { ChartData } from '../types';

import Card from '../components/Card';
import { Loading, LoadingBlock } from '../components/Loading';
import Button from '../components/Button';
import { ROLES } from '../constants';
import Chart from '../components/Chart';
import SwitchButton from '../components/Switch';
import { getGAtrackingEnabled, setGAtrackingEnabled } from '../util';
import Input from '../components/Input';
import {
  useGetSchoolsQuery,
  useGetEmailHealthStatsQuery,
} from '../services/apiSlice';

const BaseKPIDashboard: FC = () => {
  const {
    rootStore: { uiStore, statsStore },
  } = useStores();
  const {
    recalculateAllDashboardData,
    flushAllRedisCache,
    pushTroughAllReviews,
    startBenchmark,
    backgroundProcessingStatus,
  } = statsStore;
  const { user } = useAppSelector((state) => state.user);
  const isOwner = useAppSelector((state) =>
    selectHasOneOfTheRoles(state, [ROLES.owner]),
  );
  const [schools, setSchools] = useState<string[]>([]);
  const [schoolFilter, setSchoolFilter] = useState<string>();
  const [chartData, setChartData] = useState<ChartData | undefined>();
  const [submissionCardsData, setSubmissionCardsData] = useState<{
    cumulative: number;
    review: number;
  }>();
  const [enabled, setEnabled] = useState(false);
  const [revenueLoading, setRevenueLoading] = useState(isOwner);
  const [statsLoading, setStatsLoading] = useState(true);
  const defaultBenchmarkStudentCount = 1000;
  const [benchmarkStudentCount, setBenchmarkStudentCount] = useState<number>(
    defaultBenchmarkStudentCount,
  );
  const { refetch: refetchSchoolsData } = useGetSchoolsQuery();
  const { data: emailHealthData } = useGetEmailHealthStatsQuery();

  const handleToggle = () => {
    uiStore.setRefreshing(true);
    const setGAtracking = async () => {
      const setGAtrackingEnabledResult = await setGAtrackingEnabled(!enabled);
      setEnabled(() => !!setGAtrackingEnabledResult);
      uiStore.setRefreshing(false);
    };
    setGAtracking();
  };

  useEffect(() => {
    uiStore.setRefreshing(false);
    // Remove double didLoad call
    uiStore.shouldShowKPIModal(false);
    setSchools([]);
    setSchoolFilter('');

    // Set didLoad immediately to show page structure
    uiStore.didLoad(true);

    // Load all data in parallel, but handle each independently
    refetchSchoolsData()
      .then((result) => {
        if (result.data) {
          setSchools(result.data);
        }
      })
      .catch((error) => {
        console.error('Failed to load schools:', error);
      });

    // Load KPI data and update its loading state when done
    statsStore
      .getKPIData('')
      .then(() => {
        setStatsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load KPI data:', error);
        setStatsLoading(false);
      });

    // Load revenue data separately if user is owner
    if (isOwner) {
      statsStore
        .getRevenueData()
        .then(() => {
          setRevenueLoading(false);
        })
        .catch((error) => {
          console.error('Failed to load revenue data:', error);
          setRevenueLoading(false);
        });
    } else {
      setRevenueLoading(false);
    }

    // Load GA tracking setting
    getGAtrackingEnabled()
      .then((result) => {
        setEnabled(!!result);
      })
      .catch((error) => {
        console.error('Failed to load GA tracking setting:', error);
        setEnabled(false);
      });
  }, [isOwner]);

  useEffect(() => {
    if (!schoolFilter || schoolFilter === '') {
      return;
    }
    uiStore.setRefreshing(true);
    const getKPIData = async () => {
      await statsStore.getKPIData(schoolFilter);
      if (statsStore.kpiData.school.submissionRates) {
        const { cumulative, review, ...rest } =
          statsStore.kpiData.school.submissionRates;
        setSubmissionCardsData(() => ({
          cumulative,
          review,
        }));
        setChartData(() => ({
          labels: Object.keys(rest),
          datasets: [
            {
              data: Object.values(rest).map((v) => v * 100),
              borderColor: 'rgb(53, 162, 235)',
              backgroundColor: 'rgba(53, 162, 235, 0.5)',
            },
          ],
        }));
      }
      uiStore.setRefreshing(false);
    };
    getKPIData();
  }, [schoolFilter]);

  const customStyles = {
    content: {
      overflow: 'hidden',
      borderRadius: '12px',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      outerWidth: '50px',
      transform: 'translate(-50%, -50%)',
    },
  };

  const fullRecalculationTime =
    uiStore.isRecalculating && backgroundProcessingStatus.isLocked
      ? backgroundProcessingStatus.elapsedTimeSec
      : statsStore.kpiData.fullRecalcTime[0]?.duration;

  return (
    <div className="grid gap-12">
      {/* Revenue & Subscriptions Section */}
      {isOwner && (
        <div
          data-testid="revenue-section"
          className={`grid grid-cols-1 gap-6 px-6 pb-6 pt-3 shadow-2xl rounded-xl border-tttDefault border-[1px]`}
        >
          <div
            className={`grid justify-self-center max-w-[90%] justify-items-center items-center text-center overflow-hidden uppercase text-white font-bold md:text-xl lg:text-2xl p-3 -mt-7 shadow-md rounded-xl bg-tttDefault`}
          >
            Revenue & Subscriptions
          </div>
          <div className="absolute right-8">
            {uiStore.isRefreshing && <Loading />}
          </div>
          {revenueLoading ? (
            <div className="flex justify-center items-center py-12 md:px-6">
              <LoadingBlock />
            </div>
          ) : (
            <div className="grid gap-6 md:px-6 md:py-6">
              {/* Top row - Key metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card
                  id="arr-card"
                  title={'Annual Recurring Revenue (pre-tax)'}
                  body={statsStore.kpiData.revenue?.currentARR}
                  tooltipText={
                    'Total annual value of active subscriptions after discounts, before taxes'
                  }
                  plainNumber={true}
                  preFix={'$'}
                />
                <Card
                  id="active-users-card"
                  title={'Active Users (30 days)'}
                  body={statsStore.kpiData.activeUsersLast30Days}
                  tooltipText={
                    'Students and thinkers who logged in within the last 30 days'
                  }
                  plainNumber={true}
                />
                <Card
                  id="email-health-card"
                  title={'Email Health'}
                  body={
                    emailHealthData
                      ? 100 - emailHealthData.percentageWithIssues
                      : undefined
                  }
                  tooltipText={
                    emailHealthData
                      ? `${emailHealthData.problematic} of ${emailHealthData.total} users have email issues (${emailHealthData.bounced} bounced, ${emailHealthData.complained} complained)`
                      : 'Percentage of users with valid email addresses'
                  }
                  plainNumber={true}
                  postFix={'%'}
                />
                <Card
                  id="churn-rate-card"
                  title={'Churn Rate'}
                  body={statsStore.kpiData.revenue?.churnRate}
                  tooltipText={
                    'Percentage of subscriptions cancelled in the last 30 days'
                  }
                  plainNumber={true}
                  postFix={'%'}
                  trend={statsStore.kpiData.revenue?.churnRateTrend}
                />
              </div>
              {/* Middle row - Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <>
                  <Chart
                    id="arr-growth-chart"
                    key={JSON.stringify(
                      statsStore.kpiData.revenue?.weeklyARRHistory,
                    )}
                    title={'Weekly ARR Growth'}
                    data={
                      statsStore.kpiData.revenue?.weeklyARRHistory
                        ? {
                            labels:
                              statsStore.kpiData.revenue.weeklyARRHistory.map(
                                (w) => w.week,
                              ),
                            datasets: [
                              {
                                data: statsStore.kpiData.revenue.weeklyARRHistory.map(
                                  (w) => w.arr,
                                ),
                                borderColor: 'rgb(34, 197, 94)',
                                backgroundColor: 'rgba(34, 197, 94, 0.5)',
                                label: 'ARR ($)',
                              },
                            ],
                          }
                        : undefined
                    }
                    baseType="line"
                    loading={!statsStore.kpiData.revenue?.weeklyARRHistory}
                    customScales={{
                      xAxis: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value: any) {
                            return '$' + value.toLocaleString();
                          },
                        },
                      },
                    }}
                    customTooltipLabel={(context: any) => {
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label +=
                          '$' +
                          context.parsed.y
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      }
                      return label;
                    }}
                  />
                  <Chart
                    id="subscription-breakdown-chart"
                    key={JSON.stringify(
                      statsStore.kpiData.revenue?.activeSubscriptions,
                    )}
                    title={'Active Subscriptions'}
                    data={
                      statsStore.kpiData.revenue?.activeSubscriptions
                        ? {
                            labels: ['Monthly', 'Quarterly', 'Yearly', 'Trial'],
                            datasets: [
                              {
                                data: [
                                  statsStore.kpiData.revenue.activeSubscriptions
                                    .monthly,
                                  statsStore.kpiData.revenue.activeSubscriptions
                                    .quarterly,
                                  statsStore.kpiData.revenue.activeSubscriptions
                                    .yearly,
                                  statsStore.kpiData.revenue.activeSubscriptions
                                    .trial,
                                ],
                                backgroundColor: [
                                  'rgba(54, 162, 235, 0.8)',
                                  'rgba(255, 206, 86, 0.8)',
                                  'rgba(75, 192, 192, 0.8)',
                                  'rgba(153, 102, 255, 0.8)',
                                ],
                                borderColor: [
                                  'rgba(54, 162, 235, 1)',
                                  'rgba(255, 206, 86, 1)',
                                  'rgba(75, 192, 192, 1)',
                                  'rgba(153, 102, 255, 1)',
                                ],
                                borderWidth: 1,
                              },
                            ],
                          }
                        : undefined
                    }
                    baseType="doughnut"
                    loading={!statsStore.kpiData.revenue?.activeSubscriptions}
                  />
                </>
              </div>
            </div>
          )}
        </div>
      )}
      <div
        data-testid="app-stats-section"
        className={`grid grid-cols-1 gap-6 px-6 pb-6 pt-3 shadow-2xl rounded-xl border-tttDefault border-[1px]`}
      >
        <div
          className={`grid justify-self-center max-w-[90%] justify-items-center items-center text-center overflow-hidden uppercase text-white font-bold md:text-xl lg:text-2xl p-3 -mt-7 shadow-md rounded-xl bg-tttDefault`}
        >
          Application Statistics
        </div>
        <div className="absolute right-8">
          {uiStore.isRefreshing && <Loading />}
        </div>
        <div
          className={`grid justify-items-start grid-flow-row md:grid-flow-col md:grid-rows-[auto_1fr] gap-6 md:px-6 md:py-6 w-full`}
        >
          <div className="grid max-w-24 w-full md:w-[70%] md:col-start-1 gap-6 items-start">
            {statsLoading ? (
              <>
                <LoadingBlock />
                <LoadingBlock />
              </>
            ) : (
              <>
                <Card
                  id="total-students-card"
                  title={'Total number of Students'}
                  body={statsStore.kpiData.totalStudents}
                  tooltipText={'Excluding demo students'}
                  plainNumber={true}
                />
                <Card
                  id="total-results-card"
                  title={'Total number of Result Submissions'}
                  body={statsStore.kpiData.totalResults}
                  tooltipText={
                    'For the current academic year, Excluding demo results'
                  }
                  plainNumber={true}
                />
              </>
            )}
          </div>
          <div
            className={`grid w-full gap-12 md:row-start-1 ${
              schoolFilter && chartData && 'md:row-span-2'
            } md:col-start-2`}
          >
            <div data-testid="school-select">
              <Select
                key={'school-select'}
                value={
                  schoolFilter
                    ? {
                        value: schoolFilter,
                        label: schoolFilter,
                      }
                    : undefined
                }
                onChange={(newValue) => {
                  setSchoolFilter(() => newValue?.label);
                }}
                placeholder={'Click to select a school'}
                options={schools?.map((school) => ({
                  value: school,
                  label: school,
                }))}
                className="justify-self-start w-[100%] h-max"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderWidth: 2,
                    borderColor: 'rgba(0, 105, 217)',
                  }),
                }}
              />
            </div>
            {!!schoolFilter && chartData && (
              <>
                <Chart
                  id="school-submission-rate-chart"
                  key={JSON.stringify(chartData)}
                  title={'Submission rates'}
                  data={chartData}
                />
                <div className="grid grid-cols-3 gap-6">
                  <Card
                    id="school-cumulative-card"
                    title="Cumulative score"
                    body={
                      statsStore.kpiData.school?.overallResult
                        ? statsStore.kpiData.school.overallResult * 100
                        : undefined
                    }
                  />
                  <Card
                    id="school-submission-rate-card"
                    title={'Cumulative submission rate'}
                    body={submissionCardsData?.cumulative}
                    plainNumber={true}
                    precisePct={true}
                  />
                  <Card
                    id="school-review-submission-rate-card"
                    title={'Review submission rate'}
                    body={submissionCardsData?.review}
                    plainNumber={true}
                    precisePct={true}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        data-testid="db-metrics-section"
        className={`grid grid-cols-1 gap-6 px-6 pb-6 pt-3 shadow-2xl rounded-xl border-tttDefault border-[1px]`}
      >
        <div
          className={`grid justify-self-center max-w-[90%] justify-items-center items-center text-center overflow-hidden uppercase text-white font-bold md:text-xl lg:text-2xl p-3 -mt-7 shadow-md rounded-xl bg-tttDefault`}
        >
          Database Health Metrics
        </div>
        <div className="md:text-lg font-medium md:px-6">
          Dashboard data recalculation times
        </div>
        <div
          className={`grid grid-flow-row md:grid-flow-col md:grid-cols-3 gap-6 md:gap-16 md:px-6 md:pb-6 w-full`}
        >
          {statsLoading ? (
            <>
              <LoadingBlock />
              <LoadingBlock />
              <LoadingBlock />
            </>
          ) : (
            <>
              <Card
                id="student-recalc-card"
                title={'Student dashboard'}
                body={statsStore.kpiData.studentRecalcTime[0]?.duration}
                tooltipText={
                  'Recalculation time for a single student after submitting a result. This is not blocking the review for the student.'
                }
                plainNumber={true}
                postFix={' sec'}
              />
              <Card
                id="school-class-teacher-recalc-card"
                title={'School/class/teacher dashboard'}
                body={
                  statsStore.kpiData.schoolClassStudentRecalcTime[0]?.duration
                }
                tooltipText={
                  'Recalculation time for the teachers and school admins after a student submitted a result.'
                }
                plainNumber={true}
                postFix={' sec'}
              />
              <Card
                id="complete-recalc-card"
                title={'Complete dashboard data '}
                body={fullRecalculationTime}
                tooltipText={
                  'Full recalculation time. You may trigger this below manually and it runs periodically scheduled as well.'
                }
                plainNumber={true}
                postFix={' sec'}
              />
            </>
          )}
        </div>
      </div>
      {isOwner && (
        <div
          data-testid="app-settings-section"
          className={`grid grid-cols-1 gap-6 px-6 pb-6 pt-3 shadow-2xl rounded-xl border-tttDefault border-[1px]`}
        >
          <div
            className={`grid justify-self-center max-w-[90%] justify-items-center items-center text-center overflow-hidden uppercase text-white font-bold md:text-xl lg:text-2xl p-3 -mt-7 shadow-md rounded-xl bg-tttDefault`}
          >
            Application Settings
          </div>
          <div className="absolute right-8">
            {uiStore.isRefreshing && <Loading />}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-6 pb-6 pt-3 w-full text-center ">
            <div className="grid grid-rows-2 items-center">
              <p className="font-medium">Recalculate all dashboard data</p>
              <Button
                id="recalculate-button"
                action={() =>
                  uiStore.shouldShowKPIModal(
                    true,
                    recalculateAllDashboardData.bind(statsStore),
                  )
                }
                bgColor={'bg-rose-500'}
                bgColorHover={'bg-rose-600'}
                buttonText={'Recalculate'}
                textColor={'text-white'}
                disabled={uiStore.isRefreshing}
                disabledProgress={
                  uiStore.isRecalculating
                    ? backgroundProcessingStatus.progress
                    : undefined
                }
              />
            </div>
            <div className="grid grid-rows-2 items-center">
              <p className="font-medium">Flush all Redis cache</p>
              <Button
                id="flush-redis-button"
                action={() =>
                  uiStore.shouldShowKPIModal(
                    true,
                    flushAllRedisCache.bind(statsStore),
                  )
                }
                bgColor={'bg-rose-500'}
                bgColorHover={'bg-rose-600'}
                buttonText={'Flush'}
                textColor={'text-white'}
                disabled={uiStore.isRefreshing}
              />
            </div>
            <div className="grid grid-rows-2 items-center">
              <p className="font-medium">
                Push all students trough the last review
              </p>
              <Button
                id="push-through-review-button"
                action={() =>
                  uiStore.shouldShowKPIModal(
                    true,
                    pushTroughAllReviews.bind(statsStore),
                    'pushTroughAll',
                  )
                }
                bgColor={'bg-rose-500'}
                bgColorHover={'bg-rose-600'}
                buttonText={'Push trough'}
                textColor={'text-white'}
                disabled={uiStore.isRefreshing}
              />
            </div>
            <div className="grid grid-rows-2 gap-3 justify-items-center items-center">
              <p className="font-medium">Google Analytics Tracking</p>
              <SwitchButton
                id="GA-button"
                checked={enabled}
                loading={uiStore.isRefreshing}
                onChange={handleToggle}
                enabledText="Enabled"
                disabledText="Disabled"
              />
            </div>
            {user?.email === 'zsolt.bako@toptal.com' && (
              <>
                <div className="grid grid-rows-2 items-center">
                  <p className="font-medium">Benchmarking</p>
                  <div className="flex flex-row">
                    <Input
                      name="benchmark-student-count"
                      type="number"
                      value={benchmarkStudentCount}
                      min={1}
                      max={10_000}
                      disabled={uiStore.isRefreshing}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setBenchmarkStudentCount(Number(event.target.value));
                      }}
                      style={{
                        fontSize: '16px',
                        letterSpacing: '3px',
                        textAlign: 'right',
                        width: '50%',
                      }}
                    />
                    <div className="self-center mb-4 ml-2">students</div>
                  </div>
                  <Button
                    id="run-benchmark-button"
                    action={() =>
                      uiStore.shouldShowKPIModal(
                        true,
                        startBenchmark.bind(statsStore, benchmarkStudentCount),
                      )
                    }
                    bgColor={'bg-rose-500'}
                    bgColorHover={'bg-rose-600'}
                    buttonText={'Run benchmark'}
                    textColor={'text-white'}
                    disabled={uiStore.isRefreshing}
                    disabledProgress={
                      uiStore.isBenchmarking
                        ? Math.round(
                            backgroundProcessingStatus.progressPercentage * 100,
                          ) + ' %'
                        : undefined
                    }
                  />
                </div>
                {backgroundProcessingStatus.progressPercentage ? (
                  <div className="grid grid-rows-2 items-center">
                    <div>{backgroundProcessingStatus.progress}</div>
                  </div>
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <Modal
        isOpen={uiStore.KPIModal.show}
        onRequestClose={() => uiStore.shouldShowKPIModal(false)}
        style={customStyles}
      >
        <>
          <div
            className="absolute grid justify-items-end w-full pr-8 cursor-pointer"
            onClick={() => uiStore.shouldShowKPIModal(false)}
          >
            <XIcon className="h-5 w-5" />
          </div>
          <div className="text-sm text-slate-500 w-[95%] break-words max-w-[400px] p-3">
            {uiStore.KPIModal.actionName === 'pushTroughAll' &&
              'This action pushes ALL students through their last unfinished reviews.'}
            {uiStore.KPIModal.actionName !== 'pushTroughAll' &&
              'This action has a temporary performance impact on the application.'}
            <br />
            It cannot be undone. Do you want to proceed?
          </div>
          <div className="grid justify-items-center items-center md:mt-0">
            <Button
              action={() => {
                if (uiStore.KPIModal.action) {
                  uiStore.KPIModal.action();
                }
                uiStore.shouldShowKPIModal(false);
              }}
              bgColor={'bg-blue-300'}
              bgColorHover={'bg-blue-400'}
              buttonText={'Continue'}
              disabled={uiStore.isRefreshing}
            />
          </div>
        </>
      </Modal>
    </div>
  );
};

const KPIDashboard = observer(BaseKPIDashboard);
export default KPIDashboard;
